import React from "react";
import { motion } from "framer-motion";

const STEPS = [
  {
    title: "Select Jurisdiction & Business Activity",
    copy: "Choose between UAE mainland, freezone or offshore based on your target market, ownership preference and budget. Define your business activity to determine the correct license type.",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Document Preparation & Trade License",
    copy: "Submit the required documents — passport copies, MOA and activity details — to the relevant authority. Obtain your official UAE trade license issued by DED or the respective freezone authority.",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Visa Process",
    copy: "Apply for your UAE residency visa as a business owner or investor. The process includes an entry permit, medical fitness test, Emirates ID registration and visa stamping.",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Corporate Bank Account Opening",
    copy: "Open a UAE corporate bank account with a licensed local or international bank to manage business transactions, receive payments and establish financial credibility in the UAE market.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
  },
];

export default function ProcessV2() {
  return (
    <section
      data-testid="v2-process"
      className="relative bg-white py-10 md:py-14"
    >
      <div className="container-x">
        <div className="max-w-3xl mb-7">
          <p className="overline text-[#3366FF]">How to start</p>
          <div className="gold-rule mt-5 mb-6" />
          <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
            How to start a business in the UAE —{" "}
            <span className="font-black text-[#002366]">4 simple steps</span>.
          </h2>
        </div>

        <div className="space-y-10 md:space-y-14">
          {STEPS.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={s.title}
                data-testid={`v2-process-step-${i}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-100px" }}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <motion.div
                  variants={{
                    hidden: { opacity: 0, x: reverse ? 40 : -40 },
                    show: {
                      opacity: 1,
                      x: 0,
                      transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
                    },
                  }}
                  className="lg:col-span-7"
                >
                  <div className="relative rounded-sm overflow-hidden group">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[180px] md:h-[240px] object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#002366]/40 via-transparent to-transparent" />
                    <span className="absolute top-4 left-5 font-heading font-black text-white text-4xl md:text-6xl leading-none drop-shadow-[0_4px_30px_rgba(0,0,0,0.35)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  variants={{
                    hidden: { opacity: 0, x: reverse ? -30 : 30 },
                    show: {
                      opacity: 1,
                      x: 0,
                      transition: { duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] },
                    },
                  }}
                  className="lg:col-span-5"
                >
                  <div className="overline text-[#3366FF]">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-4 font-heading font-light tracking-tight text-[#0F172A] text-3xl md:text-4xl leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-slate-600 leading-relaxed">{s.copy}</p>
                  <div className="mt-8 h-px w-16 bg-[#3366FF]" />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
